import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';

import HtmlRenderer from '@/components/HtmlRender';
import useDeviceDetect from '@/helpers/hooks/useDeviceDetect';
import useEzShip from '@/helpers/hooks/useEzShip';
import cn from '@/helpers/utils/classNames';
import { loadApproperiateImage } from '@/helpers/utils/cloudflare';
import { useAppSelector } from '@/store';

import Button from '../Button';

const Badge = dynamic(() => import('../Badge'));

export type BaseProductCardProps = {
  name: string;
  href: string;
  price: string;
  thumbnail: string;
  className?: string;
  isOutOfStock?: boolean;
  pawClubPromotionIcon?: string;
  productImgClassName?: string;
  productTitleClassName?: string;
  isCartOperationsDisabled?: boolean;
  productImgContainerClassName?: string;
  onClick?: () => void;
} & (
  | {
      isPromotionPDFVisible?: false;
      promotionPDF?: string;
    }
  | {
      isPromotionPDFVisible: boolean;
      promotionPDF: string;
    }
) &
  (
    | {
        isPromotionFeatureVisible?: false;
        promotionFeature?: string;
      }
    | {
        isPromotionFeatureVisible: boolean;
        promotionFeature: string;
      }
  ) &
  (
    | {
        hasStrikeOutPrice?: false;
        strikeOutPrice?: string;
      }
    | {
        strikeOutPrice: string;
        hasStrikeOutPrice: boolean;
      }
  ) &
  (
    | {
        showQualifyingBadge?: false;
        freeShippingImage?: string;
      }
    | {
        showQualifyingBadge: boolean;
        freeShippingImage: string;
      }
  );

export const BaseProductCard = ({
  name,
  className,
  pawClubPromotionIcon,
  isOutOfStock,
  showQualifyingBadge,
  freeShippingImage,
  productImgContainerClassName,
  href: url,
  thumbnail,
  productImgClassName,
  isCartOperationsDisabled,
  productTitleClassName,
  hasStrikeOutPrice,
  price: priceToDisplay,
  strikeOutPrice,
  isPromotionFeatureVisible,
  promotionFeature,
  isPromotionPDFVisible,
  promotionPDF,
  onClick,
}: BaseProductCardProps) => {
  const { isMobile } = useDeviceDetect();
  const { ezShipId, generateURL: generateEzShipURL } = useEzShip();
  const isEzShipFlow = !!ezShipId;
  const action = useAppSelector(state => state.slices.ezShipSlice.action);

  const isEzShipCreating = action.name === 'addToEzShip' && ezShipId?.toString() === '0';

  return (
    <div className={cn('v2-group/product-card v2-w-full', className)}>
      <div className="v2-relative v2-mb-3">
        <div className="v2-absolute sm:v2-top-[15px] v2-top-[12px] sm:v2-left-[15px] v2-left-[12px] v2-flex v2-flex-col v2-gap-2">
          {pawClubPromotionIcon && !isOutOfStock && (
            <Image
              src={loadApproperiateImage(pawClubPromotionIcon, 'public')}
              alt="Left Side Icon"
              className="v2-w-[56px] v2-h-[56px] sm:v2-w-[63px] sm:v2-h-[63px] v2-rounded-full"
              width={63}
              height={63}
            />
          )}
          {isOutOfStock && <Badge label="Out of Stock" variant="filled" scheme="danger" />}
        </div>
        {showQualifyingBadge && freeShippingImage && (
          <Image
            src={freeShippingImage}
            alt="Qualifying Item Badge"
            className="v2-absolute sm:v2-top-[15px] v2-top-[8px] sm:v2-right-[15px] v2-right-[8px] v2-w-[40px] v2-h-[40px] sm:v2-w-[63px] sm:v2-h-[63px] v2-rounded-full"
            width={63}
            height={63}
          />
        )}
        <Link
          href={url}
          className={cn(
            'v2-max-h-[360px] xs:v2-max-h-[217px] sm:v2-max-h-[217px] md:v2-max-h-[360px] v2-min-h-[360px] md:v2-min-h-[360px] xs:v2-min-h-[217px] sm:v2-min-h-[217px] v2-rounded v2-bg-[#F4F5F5] v2-flex',
            productImgContainerClassName
          )}
          onClick={onClick}
        >
          {thumbnail && (
            <Image
              src={loadApproperiateImage(thumbnail, isMobile ? 'mobile' : 'public')}
              alt="product image"
              className={cn('v2-rounded v2-mx-auto v2-block v2-object-contain', productImgClassName)}
              width={293}
              height={293}
              layout="responsive"
              quality={100}
            />
          )}
        </Link>
        <div className="v2-absolute v2-bottom-0 v2-z-10 v2-hidden  v2-w-full v2-flex-col v2-items-center v2-justify-end v2-gap-2 xs:v2-px-4 sm:v2-px-4 md:v2-px-6 v2-px-6 v2-pb-5 group-hover/product-card:v2-flex">
          {isEzShipFlow ? (
            <Button
              scheme="primary"
              variant="filled"
              btnSize="md"
              type="button"
              className="xs:v2-text-[14px] w-full text-center"
              disabled={isOutOfStock || isCartOperationsDisabled || isEzShipCreating}
              as="Link"
              href={url}
              onClick={onClick}
            >
              <span>View Details</span>
            </Button>
          ) : (
            <Button
              scheme="primary"
              variant="filled"
              btnSize="md"
              type="button"
              disabled={isOutOfStock || isCartOperationsDisabled}
              className="xs:v2-text-[14px] w-full text-center"
              as="Link"
              href={url}
              onClick={onClick}
            >
              <span>View Details</span>
            </Button>
          )}
        </div>
      </div>
      <hr className="v2-my-[15px]" />
      <div className="v2-mt-[15px]">
        <h4
          className={cn(
            'v2-font-sweet-sans-pro-light v2-mb-0 v2-text-lg md:v2-text-lg xs:v2-text-base sm:v2-text-base v2-font-light v2-text-zinc-800 v2-min-h-[56px] md:v2-min-h-[56px] xs:v2-min-h-[48px] sm:v2-min-h-[48px]',
            productTitleClassName
          )}
        >
          {name ?? ''}
        </h4>
        <p className="v2-mt-2 v2-font-sweet-sans-pro-semi-bold v2-text-lg md:v2-text-lg xs:v2-text-base sm:v2-text-base v2-font-semibold">
          {hasStrikeOutPrice && <span className="v2-line-through v2-text-zinc-800 v2-mr-2">{strikeOutPrice}</span>}

          <span
            className={cn({
              'v2-text-error-600': hasStrikeOutPrice,
              'v2-text-zinc-800': !hasStrikeOutPrice,
            })}
          >
            {priceToDisplay}
          </span>
        </p>
        {isPromotionFeatureVisible && promotionFeature && (
          <p className="v2-font-sweet-sans-pro-semi-bold v2-text-lg md:v2-text-lg xs:v2-text-base sm:v2-text-base v2-font-semibold v2-text-error-600  v2-flex v2-gap-1">
            <HtmlRenderer
              as="span"
              className="v2-font-sweet-sans-pro-semi-bold v2-text-lg md:v2-text-lg xs:v2-text-base sm:v2-text-base v2-font-semibold v2-text-error-600 "
              html={promotionFeature}
            />
            {isPromotionPDFVisible && promotionPDF && (
              <Link href={promotionPDF} target="_blank">
                <span className="v2-ml-1 v2-text-sm v2-capitalize v2-text-gray-900 v2-underline">details</span>
              </Link>
            )}
          </p>
        )}
      </div>
    </div>
  );
};
